<template>
	<div
		data-language-switcher
		id="language-switcher"
	>
	<button
		type="button"
		data-dropdown
		ref="dropdown"
		id="language-switcher-dropdown"
		v-click-outside="focusOut"
		@click.stop="dropdownClicked"
		@keydown.enter.prevent="dropdownClicked"
		@keydown.space.prevent="dropdownClicked"
		@keydown.down.prevent="focusFirstOption"
		:aria-expanded="selectboxIsExpanded.toString()"
		aria-haspopup="radiogroup"
		aria-controls="language-list"
		aria-labelledby="language-switcher-preview-desc"
	>
		<font-awesome-icon
			icon="globe"
			aria-hidden="true"
		/>
		<span hidden id="language-switcher-preview-desc">{{ this.$store.getters.textResource(`rs_language_switcher_aria_info`) }}</span>
		<span id="language-switcher-preview">
			{{ selectedLanguage || surveyLanguage }}
		</span>
		<font-awesome-icon
			data-icon
			:icon="selectboxIsExpanded ? 'angle-up' : 'angle-down'"
			aria-hidden="true"
		/>
	</button>
	<ul
		v-show="selectboxIsExpanded"
		id="language-list"
		class="language-list"
		role="radiogroup"
		:aria-label="this.$store.getters.textResource(`rs_language_switcher_select_language`)"
	>
		<li
			v-for="(language, index) in languages"
			:key="language.culture"
			class="language-item"
		>
		<label
			class="language-option"
		>
			<input
				ref="radio"
				:id="`lang-${language.culture}`"
				type="radio"
				name="language"
				:checked="currentLanguage.culture === language.culture"
				@change="optionSelected(language, $event)"
				@keydown.down.prevent="moveFocus(index, `next`)"
				@keydown.up.prevent="moveFocus(index, `previous`)"
				@keydown.esc.prevent="closeDropdown"
			/>
			<span
				class="radio"
				aria-hidden="true"
			></span>
			<span class="label">
				{{ language.translatedName }}
			</span>
		</label>
		</li>
	</ul>
	<transition name="fade">
		<div
			v-if="showDialog"
			data-dialog-background
			@click.self="dialogBackgroundClicked"
		>
		<div
			data-dialog
			role="dialog"
			aria-modal="true"
			aria-labelledby="language-switcher-header"
			aria-describedby="language-switcher-subtitle"
			ref="dialog"
			@keydown.esc.prevent="dialogBackgroundClicked"
			@keydown.tab="trapFocus"
		>
			<header>
			<strong id="language-switcher-header">
				{{ confirmedLanguageVariables.header }}
			</strong>
			</header>
			<main>
				<p
					id="language-switcher-subtitle"
					role="status"
					aria-live="polite"
				>
				{{ confirmedLanguageVariables.subtitle }}
			</p>
			</main>
			<footer>
			<navbutton
				:is-navigation="false"
				@element-click="cancelButtonClicked"
				:text="confirmedLanguageVariables.cancel"
				styling="tertiary"
				id="cancel"
			/>
			<navbutton
				:is-navigation="false"
				:text="confirmedLanguageVariables.confirm"
				@element-click="saveButtonClicked"
				styling="secondary"
				id="save"
			/>
			</footer>
		</div>
	</div>
	</transition>
</div>
</template>
<script>
import Navbutton from "./Navbutton.vue";
import methods from "@/global/methods.js";

export default {
	name: "language-switcher",
	components: { Navbutton },
	data() {
		return {
			selectboxIsExpanded: false,
			selectedLanguage: ``, // The language that has been selected in the popup
			surveyLanguage: ``, // The language the survey currently is in. The survey won't change language until the popup is gone
			confirmedLanguageVariables: {
				header: "",
				subtitle: "",
				confirm: "",
				cancel: "",
			},
			showDialog: false,
			fromLogin: false,
			changeLanguageArgs: {
				language: ``,
				fromLogin: ``
			}
		};
	},
	mounted() {
		this.surveyLanguage = this.selectedLanguage = this.currentLanguage.translatedName;
		this.fromLogin = this.$route.name === `Beginpage` || this.$route.name === `Loginpage`;

		document.addEventListener("click", this.focusOut);
	},
	beforeDestroy() {
		document.removeEventListener("click", this.focusOut);
	},
	computed: {
		state() {
			return this.$store.state;
		},
		surveyInfo() {
			return this.state.surveyInfo;
		},
		languages() {
			return this.state.languages;
		},
		currentLanguage() {
			if (this.selectedLanguage !== ``) {
				return this.languages.find((l) => l.name === this.selectedLanguage || l.translatedName === this.selectedLanguage);
			}

			return this.languages.find((x) => x.culture === this.surveyInfo.culture);
		},
	},
	watch: {
		showDialog(isOpen) {
			if (isOpen) {
				this.$nextTick(() => {
					const firstButton = this.$refs.dialog?.querySelector("button");

					if (firstButton) {
						firstButton.focus();
					}
				});
			} 
			else {
				this.$nextTick(() => {
					this.$refs.dropdown?.focus();
				});
			}
		},
	},
	methods: {
		dropdownClicked() {
			this.selectboxIsExpanded = !this.selectboxIsExpanded;

			if (this.selectboxIsExpanded) {
				this.focusFirstOption();
			}
		},
		focusFirstOption() {
			this.$nextTick(() => {
				const radios = this.$refs.radio;

				const checkedRadio = radios.find(radio => radio.checked);
				const firstRadio = radios[0];

				(checkedRadio || firstRadio)?.focus();
			});
		},
		trapFocus(event) {
			// we don't want to tab outside of the dialog.
			const focusableElements = this.$refs.dialog?.querySelectorAll(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);

			if (!focusableElements?.length) {
				return;
			}

			const firstElement = focusableElements[0];

			const lastElement = focusableElements[focusableElements.length - 1];

			if (event.shiftKey && document.activeElement === firstElement) {
				event.preventDefault();
				lastElement.focus();
			} 
			else if (!event.shiftKey && document.activeElement === lastElement) {
				event.preventDefault();
				firstElement.focus();
			}
		},
		moveFocus(index, direction) {
			const radios = this.$refs.radio;

			if (direction === `next`) {
				const next = radios[index + 1];
				if(next === undefined) return;

				next.focus();
			}
			else if(direction === `previous`) {
				const previous = radios[index - 1];
				if(previous === undefined) return;

				previous.focus();
			}
			else return;
		},
		closeDropdown() {
			this.selectboxIsExpanded = false;

			this.$nextTick(() => {
				this.$refs.dropdown?.focus();
			});
		},
		optionSelected(language, e) {
			e.stopPropagation();

			this.selectedLanguage = language.translatedName;

			this.selectboxIsExpanded = false;
			this.showDialog = true;

			methods.xhr({
			type: "GET",
			url: `/v1/LanguageSwitcher/GetMultiLanguageMenuVariables?cultureString=${language.culture}&fromLogin=${this.fromLogin}`,
			callback: (xhr) => {
				const response = JSON.parse(xhr.responseText);

				this.confirmedLanguageVariables.header = response.header;
				this.confirmedLanguageVariables.subtitle = response.subtitle;
				this.confirmedLanguageVariables.confirm = response.confirm;
				this.confirmedLanguageVariables.cancel = response.cancel;

				this.$store.commit(`changeGlobal`, {
					variableName: `showLoader`,
					newValue: false,
				});
			},
			});
		},
		saveButtonClicked(e) {
			e.stopPropagation();
			this.showDialog = false;
			this.surveyLanguage = this.currentLanguage.culture;
			this.changeLanguageArgs.language = this.currentLanguage.language;
			this.changeLanguageArgs.fromLogin = this.fromLogin;

			this.$store.dispatch("changeLanguage", this.changeLanguageArgs);
		},
		cancelButtonClicked() {
			this.selectedLanguage = this.surveyLanguage;
			this.showDialog = false;
		},
		dialogBackgroundClicked() {
			this.showDialog = false;
			this.selectboxIsExpanded = false;
			this.selectedLanguage = this.surveyLanguage;
			this.$nextTick(() => {
				this.$refs.dropdown?.focus();
			});
		},
		focusOut(e) {
			const dropdown = this.$el.querySelector("[data-dropdown]");

			if (dropdown && !dropdown.contains(e.target)) {
				this.selectboxIsExpanded = false;
			}
		},
	},
};
</script>
<style scoped>
[data-language-switcher] {
	display: flex;
	position: absolute;
	top: 15px;
	right: 20px;
	width: 170px;
	height: 38px;
	border-radius: 64px;
	justify-self: flex-end;
	align-items: center;
	z-index: 2;
}

[data-dropdown] {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-width: 170px;
	height: 38px;
	padding: 0 14px;
	border-radius: 64px;
	border: 1px solid var(--primary-color-50);
	background: #fff;
	cursor: pointer;
	z-index: 10;
	appearance: none;
	font: inherit;
	&[aria-expanded="true"] {
		background: var(--primary-color-10);
	}
	
	#language-switcher-preview {
		margin-right: 0 10px;
	}
}

.language-list {
	position: absolute;
	top: calc(100% + 8px);
	right: 0;
	min-width: 240px;
	background: #fff;
	border-radius: 12px;
	border: 1px solid var(--primary-color-50);
	padding: 8px 0;
	margin: 0;
	z-index: 99999;
	display: flex;
	flex-direction: column;
}

.language-item {
	list-style: none;
	width: 90%;
	margin: 0 auto;
	border-bottom: 1px solid var(--primary-color-25);

	&:first-child {
		.language-option {
			border-top-left-radius: 10px;
			border-top-right-radius: 10px;
		}
	}

	&:last-child {
		.language-option {
			border-bottom-left-radius: 10px;
			border-bottom-right-radius: 10px;
		}	
	}
}

.language-item:last-of-type {
	border-bottom: none;
}

.language-option {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 10px 14px;
	cursor: pointer;
	white-space: nowrap;
}

.language-option:hover,
.language-option:focus-within {
	background: #f6f6f6;
}

.language-option input {
	position: absolute;
	opacity: 0;
	width: 1px;
	height: 1px;
	overflow: hidden;
}

.radio {
	width: 16px;
	height: 16px;
	border: 2px solid #666;
	border-radius: 50%;
	position: relative;
	flex-shrink: 0;
}

.language-option input:checked + .radio {
	border-color: var(--primary-color);
}

.language-option input:checked + .radio::after {
	content: "";
	position: absolute;
	width: 8px;
	height: 8px;
	background: var(--primary-color);
	border-radius: 50%;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.label {
	font-size: 14px;
	color: #333;
}

[data-dropdown]:focus-visible,
.language-option input:focus-visible + .radio,
[data-dialog] button:focus-visible {
	outline: 1px dotted #7e7e7e;
	outline-offset: 2px;
}

[data-dialog-background] {
	background: rgba(69, 72, 72, 0.5);
	position: fixed;
	inset: 0;
	display: flex;
	justify-content: center;
	cursor: pointer;
}

[data-dialog] {
	display: flex;
	flex-direction: column;
	min-width: 300px;
	max-width: 1000px;
	width: 100%;
	background: #fff;
	align-items: flex-start;
	align-self: center;
	border-radius: 16px;
	cursor: auto;
	white-space: initial;
}

[data-dialog] header {
	width: 100%;
	font-size: 16px;
	padding: 64px 64px 0px 64px;
	display: flex;
	flex-direction: column;
	font-family: var(--QuestionStyling-font-family-public-name);
	strong {
		font-size: calc(var(--QuestionStyling-font-size) * 1px);
		color: #000;
	}
}

[data-dialog] main {
	font-size: calc(var(--AnswerStyling-font-size) * 1px);
	width: 100%;
	padding: 0px 64px;
}

[data-dialog] footer {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: 0px 64px 64px 64px;
	margin-top: 10px;
}

[data-dialog] footer button {
	width: 100%;
	max-width: 422px;
}

.fade-enter-active,
.fade-leave-active {
	transition: all 0.4s;
}

.fade-leave-active {
	pointer-events: none;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

@media only screen and (max-width: 550px) {
	[data-dialog] {
		margin-top: 200px;
		align-self: flex-start;
		footer {
			gap: 15px;
		}
	}
}

</style>